// src/pages/AdminDashboard.js - Enhanced Admin Dashboard with Analytics
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { isAdmin } from '../utils/auth';
import { getErrorMessage } from '../utils/error';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/NotificationContainer';
import Navbar from '../components/common/Navbar';
import StatsCards from '../components/analytics/StatsCards';
import TrendChart from '../components/analytics/TrendChart';
import CategoryChart from '../components/analytics/CategoryChart';
import GeographicChart from '../components/analytics/GeographicChart';
import PatternList from '../components/patterns/PatternList';
import ExportButton from '../components/export/ExportButton';
import NewsSourcesList from '../components/news/NewsSourcesList';
import UserManagement from '../components/users/UserManagement';
import AlertList from '../components/alerts/AlertList';
import ReportDetailContent from '../components/reports/ReportDetailContent';
import DisclaimerFooter from '../components/common/DisclaimerFooter';

const REPORTS_PAGE_SIZE = 20;

const VIOLATION_TYPE_KEYS = [
  'targeting_civilians',
  'forced_displacement',
  'property_destruction',
  'deprivation_housing_aid',
  'arbitrary_arrest',
  'hate_speech_incitement',
  'other'
];

const AdminDashboard = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('analytics');
  const [reports, setReports] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '', violation_type: '' });
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [reportsTotal, setReportsTotal] = useState(0);
  const [reportsPage, setReportsPage] = useState(0);
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest'
  const [selectedReport, setSelectedReport] = useState(null);
  const [period, setPeriod] = useState('weekly');
  const [patternDetectionLoading, setPatternDetectionLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();
  const { notifications, showSuccess, showError, removeNotification } = useNotification();

  useEffect(() => {
    const id = setTimeout(() => {
      const next = searchInput.trim();
      setDebouncedSearch((prev) => {
        if (prev !== next) {
          setReportsPage(0);
        }
        return next;
      });
    }, 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    loadData();
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, filters, debouncedSearch, reportsPage, sortOrder]);

  useEffect(() => {
    if (activeTab === 'analytics') {
      loadAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, activeTab]);

  // Auto-refresh analytics every 30 seconds when on analytics tab
  useEffect(() => {
    if (activeTab === 'analytics') {
      const interval = setInterval(() => {
        loadAnalytics();
      }, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.violation_type) params.append('violation_type', filters.violation_type);
      if (debouncedSearch) params.append('q', debouncedSearch);
      params.append('limit', String(REPORTS_PAGE_SIZE));
      params.append('offset', String(reportsPage * REPORTS_PAGE_SIZE));
      params.append('sort', sortOrder === 'oldest' ? 'asc' : 'desc');

      const reportsRes = await api.get(`/moderate/reports?${params.toString()}`);
      setReports(reportsRes.data.reports || []);
      setReportsTotal(
        typeof reportsRes.data.total === 'number'
          ? reportsRes.data.total
          : reportsRes.data.count ?? 0
      );
    } catch (err) {
      console.error('Failed to load data:', err);
      showError('Failed to load reports data');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const response = await api.get(`/api/analytics/dashboard?period=${period}`);
      setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
      showError('Failed to load analytics data');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleStatusChange = async (reportId, newStatus) => {
    try {
      await api.patch(`/moderate/report/${reportId}/status`, { status: newStatus });
      showSuccess(`Report status updated to ${newStatus}`);
      loadData();
      setSelectedReport(null);
    } catch (err) {
      showError(getErrorMessage(err, 'Failed to update status'));
      console.error('Failed to update status:', err);
    }
  };

  const handleTriggerPatternDetection = async () => {
    setPatternDetectionLoading(true);
    try {
      const response = await api.post('/api/analytics/patterns/detect');
      if (response.data.success) {
        const patternCount = response.data.patterns?.length || 0;
        if (patternCount > 0) {
          showSuccess(`Pattern detection completed! Found ${patternCount} pattern(s). Alerts have been created and emails sent.`);
        } else {
          showSuccess('Pattern detection completed. No new patterns detected.');
        }
        // Reload analytics to show new patterns
        await loadAnalytics();
        // Note: PatternList will auto-refresh when the component re-mounts or when filters change
        // For immediate refresh, user can click the Refresh button in PatternList
      } else {
        showError('Pattern detection completed but returned an error');
      }
    } catch (err) {
      showError(getErrorMessage(err, 'Failed to trigger pattern detection'));
      console.error('Failed to trigger pattern detection:', err);
    } finally {
      setPatternDetectionLoading(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm(t('journalist.reports.deleteConfirm'))) return;
    setDeletingId(reportId);
    try {
      await api.delete(`/api/reports/report/${reportId}`);
      showSuccess(t('journalist.reports.deleteReport') + ' – ' + t('common.success'));
      loadData();
      if (selectedReport?.id === reportId) setSelectedReport(null);
    } catch (err) {
      showError(getErrorMessage(err, 'Failed to delete report'));
    } finally {
      setDeletingId(null);
    }
  };


  const tabs = [
    { id: 'analytics', label: 'Analytics', icon: '📊' },
    { id: 'reports', label: 'Reports', icon: '📋' },
    { id: 'patterns', label: 'Patterns', icon: '🔍' },
    { id: 'alerts', label: 'Alerts', icon: '🔔' },
    { id: 'news', label: 'News Sources', icon: '📰' },
    { id: 'users', label: 'Users', icon: '👥' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark dark:to-shayek-dark-50 transition-colors">
      <NotificationContainer 
        notifications={notifications} 
        onRemove={removeNotification} 
      />
      <Navbar showHome={true} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek mb-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
          <div className="border-b border-shayek-light-300 dark:border-shayek-dark-200 overflow-x-auto">
            <nav className="flex -mb-px flex-nowrap min-w-0">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 px-4 sm:px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-shayek-primary dark:border-shayek-red-light text-shayek-primary dark:text-shayek-red-light'
                      : 'border-transparent text-shayek-light-500 dark:text-shayek-dark-500 hover:text-shayek-light-700 dark:hover:text-shayek-dark-700 hover:border-shayek-light-300 dark:hover:border-shayek-dark-200'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <StatsCards
              statistics={analytics?.statistics}
              period={period}
              onPeriodChange={setPeriod}
              loading={analyticsLoading}
            />

            {/* Action Buttons */}
            <div className="flex justify-between items-center mb-4">
              <button
                onClick={handleTriggerPatternDetection}
                disabled={patternDetectionLoading}
                className="px-4 py-2 bg-shayek-primary dark:bg-shayek-primary-600 text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-shayek font-medium flex items-center gap-2"
              >
                {patternDetectionLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Detecting Patterns...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <span>Trigger Pattern Detection</span>
                  </>
                )}
              </button>
              <ExportButton type="analytics" period={period} />
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Trend Chart */}
              <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Report Trends</h3>
                {analyticsLoading ? (
                  <div className="flex items-center justify-center h-64 text-shayek-light-500 dark:text-shayek-dark-500">
                    Loading...
                  </div>
                ) : (
                  <TrendChart data={analytics?.trends || []} period={period} />
                )}
              </div>

              {/* Category Chart */}
              <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Category Breakdown</h3>
                {analyticsLoading ? (
                  <div className="flex items-center justify-center h-64 text-shayek-light-500 dark:text-shayek-dark-500">
                    Loading...
                  </div>
                ) : (
                  <CategoryChart data={analytics?.category_breakdown || []} />
                )}
              </div>

              {/* Geographic Chart */}
              <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 lg:col-span-2 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Geographic Distribution</h3>
                {analyticsLoading ? (
                  <div className="flex items-center justify-center h-64 text-shayek-light-500 dark:text-shayek-dark-500">
                    Loading...
                  </div>
                ) : (
                  <GeographicChart data={analytics?.geographic_distribution || []} />
                )}
              </div>

              {/* Violation Type (form_data) */}
              <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Violation Type</h3>
                {analyticsLoading ? (
                  <div className="flex items-center justify-center h-64 text-shayek-light-500 dark:text-shayek-dark-500">
                    Loading...
                  </div>
                ) : (
                  <CategoryChart data={(analytics?.violation_type_breakdown || []).map((r) => ({ category: r.type, count: r.count }))} />
                )}
              </div>

              {/* Affected Type (form_data) */}
              <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Affected Type</h3>
                {analyticsLoading ? (
                  <div className="flex items-center justify-center h-64 text-shayek-light-500 dark:text-shayek-dark-500">
                    Loading...
                  </div>
                ) : (
                  <CategoryChart data={(analytics?.affected_type_breakdown || []).map((r) => ({ category: r.type, count: r.count }))} />
                )}
              </div>

              {/* Affected Category (form_data) */}
              <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Affected Category</h3>
                {analyticsLoading ? (
                  <div className="flex items-center justify-center h-64 text-shayek-light-500 dark:text-shayek-dark-500">
                    Loading...
                  </div>
                ) : (
                  <CategoryChart data={analytics?.affected_category_breakdown || []} />
                )}
              </div>
            </div>

            {/* Recent Patterns Preview */}
            {analytics?.recent_patterns && analytics.recent_patterns.length > 0 && (
              <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Recent Patterns</h3>
                <div className="space-y-2">
                  {analytics.recent_patterns.slice(0, 5).map((pattern) => (
                    <div
                      key={pattern.id}
                      className="p-4 bg-shayek-light-50 dark:bg-shayek-dark-50 rounded-lg hover:bg-shayek-light-100 dark:hover:bg-shayek-dark-100 cursor-pointer border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors"
                      onClick={() => setActiveTab('patterns')}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="text-sm font-medium text-shayek-light-900 dark:text-shayek-dark-900">
                            {pattern.pattern_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600 mt-1">
                            Detected {new Date(pattern.detected_at).toLocaleString()}
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          pattern.severity === 'high' || pattern.severity === 'critical'
                            ? 'bg-red-100 text-red-800'
                            : pattern.severity === 'medium'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-shayek-light-100 dark:bg-shayek-dark-100 text-shayek-light-800 dark:text-shayek-dark-800'
                        }`}>
                          {pattern.severity}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
              <h2 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Filters</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
                    {t('admin.reports.search')}
                  </label>
                  <input
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder={t('admin.reports.searchPlaceholder')}
                    className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => {
                      setReportsPage(0);
                      setFilters({ ...filters, status: e.target.value });
                    }}
                    className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
                  >
                    <option value="">All</option>
                    <option value="pending">Pending</option>
                    <option value="pending_review">Pending Review</option>
                    <option value="verified">Verified</option>
                    <option value="rejected">Rejected</option>
                    <option value="flagged">Flagged</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
                    {t('admin.reports.violationType')}
                  </label>
                  <select
                    value={filters.violation_type}
                    onChange={(e) => {
                      setReportsPage(0);
                      setFilters({ ...filters, violation_type: e.target.value });
                    }}
                    className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
                  >
                    <option value="">{t('journalist.reports.filters.allCategories')}</option>
                    {VIOLATION_TYPE_KEYS.map((key) => (
                      <option key={key} value={key}>
                        {t(`report.violationTypeOptions.${key}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2 lg:col-span-4 flex items-end">
                  <button
                    type="button"
                    onClick={() => {
                      setReportsPage(0);
                      setFilters({ status: '', violation_type: '' });
                      setSearchInput('');
                    }}
                    className="px-4 py-2 text-sm text-shayek-light-700 dark:text-shayek-dark-700 bg-shayek-light-100 dark:bg-shayek-dark-50 rounded-lg hover:bg-shayek-light-200 dark:hover:bg-shayek-dark-100 transition-colors border border-shayek-light-300 dark:border-shayek-dark-200"
                  >
                    {t('admin.reports.clearFilters')}
                  </button>
                </div>
              </div>
            </div>

            {/* Reports List */}
            <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
              <div className="p-6 border-b border-shayek-light-300 dark:border-shayek-dark-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <h2 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900">
                  {t('admin.reports.totalCount', { count: reportsTotal })}
                </h2>
                <div className="flex flex-wrap items-center gap-4">
                  <select
                    value={sortOrder}
                    onChange={(e) => {
                      setReportsPage(0);
                      setSortOrder(e.target.value);
                    }}
                    className="px-3 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg text-sm bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                  </select>
                  <ExportButton
                    type="reports"
                    filters={{ ...filters, q: debouncedSearch }}
                  />
                </div>
              </div>
              {loading ? (
                <div className="p-6 text-center text-shayek-light-500 dark:text-shayek-dark-500">Loading...</div>
              ) : reports.length === 0 ? (
                <div className="p-6 text-center text-shayek-light-500 dark:text-shayek-dark-500">No reports found</div>
              ) : (
                <div className="divide-y divide-shayek-light-200 dark:divide-shayek-dark-200">
                  {reports.map((report) => (
                    <div
                      key={report.id}
                      className="p-6 hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedReport(report)}
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="px-2 py-1 text-xs font-medium bg-shayek-primary/10 dark:bg-shayek-primary/20 text-shayek-primary dark:text-shayek-red-light rounded">
                              {report.input_type}
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded ${
                              report.status === 'verified' ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400' :
                              report.status === 'rejected' ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400' :
                              report.status === 'flagged' ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400' :
                              'bg-gray-100 dark:bg-gray-900/20 text-gray-800 dark:text-gray-400'
                            }`}>
                              {report.status}
                            </span>
                            {report.category && (
                              <span className="px-2 py-1 text-xs font-medium bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-400 rounded">
                                {report.category}
                              </span>
                            )}
                          </div>
                          {report.title && (
                            <p className="text-sm font-medium text-shayek-light-900 dark:text-shayek-dark-900 mb-1 line-clamp-1">
                              {report.title}
                            </p>
                          )}
                          <p className="text-sm text-gray-700 dark:text-shayek-dark-700 mb-1 line-clamp-2 break-words overflow-hidden">{report.input_preview}</p>
                          <p className="text-xs text-gray-500 dark:text-shayek-dark-500">
                            {new Date(report.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="ml-4 flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedReport(report);
                            }}
                            className="px-3 py-1 text-sm text-shayek-primary hover:text-shayek-primary-600 dark:text-shayek-red-light dark:hover:text-shayek-red-light transition-colors"
                          >
                            View
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteReport(report.id);
                            }}
                            disabled={deletingId === report.id}
                            className="px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                            title={t('journalist.reports.deleteReport')}
                          >
                            {deletingId === report.id ? '...' : t('common.delete')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!loading && reportsTotal > 0 && (
                <div className="p-4 border-t border-shayek-light-300 dark:border-shayek-dark-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">
                    {t('admin.reports.pageOf', {
                      current: reportsPage + 1,
                      total: Math.max(1, Math.ceil(reportsTotal / REPORTS_PAGE_SIZE))
                    })}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={reportsPage <= 0}
                      onClick={() => setReportsPage((p) => Math.max(0, p - 1))}
                      className="px-4 py-2 text-sm rounded-lg border border-shayek-light-300 dark:border-shayek-dark-200 bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-100"
                    >
                      {t('admin.reports.prev')}
                    </button>
                    <button
                      type="button"
                      disabled={(reportsPage + 1) * REPORTS_PAGE_SIZE >= reportsTotal}
                      onClick={() => setReportsPage((p) => p + 1)}
                      className="px-4 py-2 text-sm rounded-lg border border-shayek-light-300 dark:border-shayek-dark-200 bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-100"
                    >
                      {t('admin.reports.next')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'patterns' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900">Pattern Detection</h2>
                <button
                  onClick={handleTriggerPatternDetection}
                  disabled={patternDetectionLoading}
                  className="px-4 py-2 bg-shayek-primary dark:bg-shayek-primary-600 text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-shayek font-medium flex items-center gap-2"
                >
                  {patternDetectionLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Detecting Patterns...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <span>Trigger Pattern Detection</span>
                    </>
                  )}
                </button>
              </div>
              <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">
                Pattern detection runs automatically every 6 hours. Click the button above to trigger it manually.
              </p>
            </div>
            <PatternList />
          </div>
        )}

        {activeTab === 'alerts' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
              <h2 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Alerts</h2>
              <AlertList isAdmin={true} />
            </div>
          </div>
        )}

        {activeTab === 'news' && (
          <NewsSourcesList />
        )}

        {activeTab === 'users' && (
          <UserManagement />
        )}
      </div>

      {/* Report Detail Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
            <div className="p-6 border-b border-shayek-light-300 dark:border-shayek-dark-200 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-shayek-light-900 dark:text-shayek-dark-900">Report Details</h3>
              <button
                onClick={() => setSelectedReport(null)}
                className="text-gray-400 dark:text-shayek-dark-500 hover:text-gray-600 dark:hover:text-shayek-dark-300 transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-shayek-dark-500">Status</label>
                <select
                  value={selectedReport.status}
                  onChange={(e) => handleStatusChange(selectedReport.id, e.target.value)}
                  className="mt-1 block w-full max-w-xs px-3 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
                >
                  <option value="pending">Pending</option>
                  <option value="pending_review">Pending Review</option>
                  <option value="verified">Verified</option>
                  <option value="rejected">Rejected</option>
                  <option value="flagged">Flagged</option>
                </select>
              </div>
              <ReportDetailContent report={selectedReport} showStatusBadge={false} />
              <div className="flex justify-end gap-2 pt-4 border-t border-shayek-light-200 dark:border-shayek-dark-200">
                <button
                  onClick={() => handleDeleteReport(selectedReport.id)}
                  disabled={deletingId === selectedReport.id}
                  className="px-4 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
                >
                  {deletingId === selectedReport.id ? '...' : t('journalist.reports.deleteReport')}
                </button>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="px-4 py-2 text-sm bg-shayek-light-200 dark:bg-shayek-dark-200 text-shayek-light-800 dark:text-shayek-dark-200 rounded-lg hover:bg-shayek-light-300 dark:hover:bg-shayek-dark-300 transition-colors"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <DisclaimerFooter />
    </div>
  );
};

export default AdminDashboard;
